import { FormControl, MenuItem, Select, type SelectChangeEvent, Box } from "@mui/material";
import { useFormContext } from "react-hook-form";
import {
  CURRENCY_CONFIG,
  updateURLWithCurrency,
  type SupportedCurrency,
} from "@utils/currencyUtils";
import { useToken } from "@contexts/TokenContext";
import { usePurchaseFlow } from "@contexts/PurchaseFlowContext";

interface CurrencySelectorProps {
  disabled?: boolean;
}

export default function CurrencySelector({ disabled = false }: CurrencySelectorProps) {
  const { watch, setValue } = useFormContext();
  const { setCurrentStep } = usePurchaseFlow();
  const selectedCurrency = watch("selectedCurrency") || "usd";
  const { isAuthenticated } = useToken();

  // Enable currency selector only when the current step is ContactInfo
  const isDisabled = disabled || isAuthenticated;

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newCurrency = event.target.value as SupportedCurrency;

    // Update form state
    setValue("selectedCurrency", newCurrency);

    // Clear currency-specific cached data
    setValue("accessoryProducts", []);
    setValue("shippingOptions", []);
    setValue("shipmentRate", null);
    setValue("totalTax", 0);

    // Reset to first step
    setCurrentStep(1);

    // Sync to URL for bookmarking/sharing
    updateURLWithCurrency(newCurrency);

    // Reset UUID for force hard reset
    setValue("uuid", null);
  };

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 120,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          color: "#fff",
          "& fieldset": {
            borderColor: "rgba(255, 255, 255, 0.3)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(255, 255, 255, 0.5)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#fff",
          },
        },
        "& .MuiSvgIcon-root": {
          color: "#fff",
        },
        "& .MuiInputLabel-root": {
          color: "rgba(255, 255, 255, 0.7)",
          "&.Mui-focused": {
            color: "#fff",
          },
        },
      }}
      disabled={isDisabled}
    >
      <Select
        labelId="currency-selector-label"
        id="currency-selector"
        value={selectedCurrency}
        onChange={handleChange}
        renderValue={(value) => {
          const config = CURRENCY_CONFIG[value as SupportedCurrency];
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: "1.2em" }}>{config.flag}</span>
              <span>{config.label}</span>
            </Box>
          );
        }}
      >
        {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
          <MenuItem key={code} value={code}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: "1.2em" }}>{config.flag}</span>
              <span>{config.fullLabel}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
